<template>
  <div class="relative w-full h-full">
    <Header :title="'Dozentenbereich'" />
    <div class="flex flex-row flex-wrap items-center justify-start mb-3">
      <filter-tab
        v-for="filter in state.filters"
        :key="filter.id"
        :filter="filter"
        :isActive="state.activeFilterId === filter.id"
        @click="state.activeFilterId = filter.id"
      />
    </div>
    <div class="grid 2xl:grid-cols-10 gap-5 pb-10">
      <dashboard-card
        id="card-courses"
        title="Kursauwahl"
        class="2xl:col-span-6"
      >
        <default-table
          v-if="GET_LECTURER_COURSES?.length > 0"
          tableWidth="1000px"
          :headers="tableHeaders"
          :items="GET_LECTURER_COURSES"
          @clickItem="FETCH_PARTICIPANTS"
        />
        <p v-else>Es konnten keine Kurse geladen werden.</p>
      </dashboard-card>
      <dashboard-card
        id="card-participants"
        :title="
          state.selectedCourseName
            ? `Kursname: ${state.selectedCourseName}`
            : 'Kursname'
        "
        class="2xl:col-span-4"
      >
        <default-table
          v-if="GET_PARTICIPANTS?.length > 0"
          tableWidth="max(100%, 450px)"
          :headers="participantsTableHeaders"
          :items="GET_PARTICIPANTS"
          @clickItem="FETCH_PARTICIPANT"
          @changeItem="handleItemChange"
        />
        <p v-else>Es konnten keine Teilnehmer geladen werden.</p>
      </dashboard-card>
      <dashboard-card
        id="card-courses-history"
        :title="
          state.selectedParticipantName
            ? `Kurshistorie: ${state.selectedParticipantName}`
            : 'Kurshistorie'
        "
        class="2xl:col-span-6"
      >
        <default-table
          v-if="GET_PARTICIPANT?.course_history?.length > 0"
          tableWidth="1000px"
          :headers="tableHeaders"
          :items="GET_PARTICIPANT.course_history"
          :itemsSelectable="false"
        />
        <p v-else>Es konnte keine Kurshistorie geladen werden.</p>
      </dashboard-card>
      <dashboard-card
        id="card-target-agreement"
        :title="
          state.selectedParticipantName
            ? `Zielvereinbarung: ${state.selectedParticipantName}`
            : 'Zielvereinbarung'
        "
        class="2xl:col-span-4"
      >
        <target-agreement-table
          v-if="GET_PARTICIPANT?.target_agreement?.length > 0"
          :targetAgreements="GET_PARTICIPANT.target_agreement"
        />
        <p v-else>Es konnte keine Zielvereinbarung geladen werden.</p>
      </dashboard-card>
    </div>
    <participant-pass-exam-modal
      :participantId="state.selectedParticipantId"
      :courseId="state.selectedCourseId"
      :isVisible="state.selectedModalName === 'participantPassExamModal'"
      @close="state.selectedModalName = ''"
    />
  </div>
</template>

<script>
import { computed, defineComponent, onMounted, reactive } from "vue";
import Header from "../../components/layout/Header.vue";
import FilterTab from "../../components/layout/FilterTab.vue";
import DashboardCard from "../../components/layout/DashboardCard.vue";
import DefaultTable from "../../components/global/DefaultTable.vue";
import TargetAgreementTable from "./TargetAgreementTable.vue";
import { useStore } from "vuex";
import { HELPER_TYPES, LECTURER_TYPES, STORE_TYPES } from "../../store/types";
import ParticipantPassExamModal from "./ParticipantPassExamModal.vue";

export default defineComponent({
  name: "LecturerDashboard",
  components: {
    Header,
    FilterTab,
    DashboardCard,
    DefaultTable,
    TargetAgreementTable,
    ParticipantPassExamModal,
  },
  setup() {
    const store = useStore();
    const state = reactive({
      filters: [
        { id: 1, text: "Meine Ausbildungen" },
        // { id: 2, text: "Rechnungen" },
        // { id: 3, text: "Anwesenheitsliste" },
        // { id: 4, text: "Lehrtagebuch" },
        // { id: 5, text: "Checkliste" },
      ],
      activeFilterId: 1,
      selectedCourseId: null,
      selectedCourseName: null,
      selectedParticipantName: null,
      selectedParticipantId: null,
      selectedModalName: null,
    });

    const tableHeaders = [
      {
        name: "Kursname",
        gridSize: 2,
        dbValue: "name",
        value: "",
      },
      {
        name: "Ort",
        gridSize: 2,
        dbValue: "location",
        value: "",
      },
      {
        name: "Startdatum",
        gridSize: 2,
        dbValue: "starts_at",
        value: "",
      },
      {
        name: "Enddatum",
        gridSize: 2,
        dbValue: "ends_at",
        value: "",
      },
    ];

    const participantsTableHeaders = [
      {
        name: "Teilnehmer",
        gridSize: 2,
        dbValue: "full_name",
        value: "",
        type: "input",
      },
      {
        name: "ZV?",
        gridSize: 0.35,
        dbValue: "target_agreement_done",
        value: "",
        type: "checkbox",
      },
      {
        name: "PB?",
        gridSize: 0.35,
        dbValue: "exam_passed",
        value: "",
        type: "checkbox",
      },
    ];

    function handleItemChange({ dbValue, itemId }) {
      if (dbValue === "exam_passed") {
        state.selectedModalName = "participantPassExamModal";
        state.selectedParticipantId = itemId;
      }
    }

    async function FETCH_LECTURER_COURSES() {
      store.commit(
        `${STORE_TYPES.HELPERS}/${HELPER_TYPES.SET_LOADING_ID}`,
        "card-courses"
      );

      return store.dispatch(
        `${STORE_TYPES.LECTURER_COURSES}/${LECTURER_TYPES.FETCH_COURSES}`
      );
    }

    async function FETCH_PARTICIPANTS(item) {
      state.selectedParticipantName = null;
      state.selectedCourseId = item.id;
      state.selectedCourseName = item.name;

      store.commit(
        `${STORE_TYPES.LECTURER_COURSES}/${LECTURER_TYPES.SET_PARTICIPANT}`,
        null
      );

      store.commit(
        `${STORE_TYPES.HELPERS}/${HELPER_TYPES.SET_LOADING_ID}`,
        "card-participants"
      );

      const response = await store.dispatch(
        `${STORE_TYPES.LECTURER_COURSES}/${LECTURER_TYPES.FETCH_PARTICIPANTS}`,
        { courseId: item.id }
      );

      store.commit(
        `${STORE_TYPES.HELPERS}/${HELPER_TYPES.REMOVE_LOADING_ID}`,
        "card-participants"
      );

      return response;
    }

    async function FETCH_PARTICIPANT(item) {
      state.selectedParticipantName = item.full_name;

      store.commit(`${STORE_TYPES.HELPERS}/${HELPER_TYPES.SET_LOADING_IDS}`, [
        "card-courses-history",
        "card-target-agreement",
      ]);

      const response = await store.dispatch(
        `${STORE_TYPES.LECTURER_COURSES}/${LECTURER_TYPES.FETCH_PARTICIPANT}`,
        { courseId: state.selectedCourseId, customerId: item.id }
      );

      store.commit(
        `${STORE_TYPES.HELPERS}/${HELPER_TYPES.REMOVE_LOADING_ID}`,
        "card-courses-history"
      );
      store.commit(
        `${STORE_TYPES.HELPERS}/${HELPER_TYPES.REMOVE_LOADING_ID}`,
        "card-target-agreement"
      );

      return response;
    }

    const GET_LECTURER_COURSES = computed(() => {
      return store.getters[
        `${STORE_TYPES.LECTURER_COURSES}/${LECTURER_TYPES.GET_COURSES}`
      ];
    });

    const GET_PARTICIPANTS = computed(() => {
      return store.getters[
        `${STORE_TYPES.LECTURER_COURSES}/${LECTURER_TYPES.GET_PARTICIPANTS}`
      ];
    });

    const GET_PARTICIPANT = computed(() => {
      return store.getters[
        `${STORE_TYPES.LECTURER_COURSES}/${LECTURER_TYPES.GET_PARTICIPANT}`
      ];
    });

    onMounted(() => {
      store.commit(
        `${STORE_TYPES.LECTURER_COURSES}/${LECTURER_TYPES.SET_PARTICIPANTS}`,
        []
      );
      store.commit(
        `${STORE_TYPES.LECTURER_COURSES}/${LECTURER_TYPES.SET_PARTICIPANT}`,
        null
      );
    });

    store.commit(
      `${STORE_TYPES.HELPERS}/${HELPER_TYPES.SET_LOADING_ID}`,
      "isLoadingData"
    );
    Promise.all([FETCH_LECTURER_COURSES()]).then(() => {
      store.commit(
        `${STORE_TYPES.HELPERS}/${HELPER_TYPES.REMOVE_LOADING_ID}`,
        "isLoadingData"
      );
      store.commit(
        `${STORE_TYPES.HELPERS}/${HELPER_TYPES.REMOVE_LOADING_ID}`,
        "card-courses"
      );
    });

    return {
      state,
      tableHeaders,
      handleItemChange,
      FETCH_PARTICIPANTS,
      FETCH_PARTICIPANT,
      GET_LECTURER_COURSES,
      GET_PARTICIPANTS,
      GET_PARTICIPANT,
      participantsTableHeaders,
    };
  },
});
</script>
